import {
  useInvalidateGetApprovalEntries,
  useInvalidateGetApprovalTimesheets,
  useInvalidateGetTimesheetRows,
  useInvalidateTimesheet,
} from '@sit/client-shared';
import { NotificationType, PushNotification } from '@sit/graphql';
import { useTimesheetSaveSettled } from '@web/api/timesheet/use-post-timesheet-save-settled';
import { useTimesheetSubmitSettled } from '@web/api/timesheet/use-post-timesheet-submit-settled';
import { PropsWithChildren, useCallback } from 'react';
import { useOnLatestNotification } from './hooks/useLatestNotification';
import { NotificationTopic } from './NotificationHandlerService';

function useHandleGlobalNotifications() {
  const onSaveSettled = useTimesheetSaveSettled();
  const onSubmitSettled = useTimesheetSubmitSettled();
  const invalidateTimesheetsQuery = useInvalidateGetApprovalTimesheets();
  const invalidateEntriesQuery = useInvalidateGetApprovalEntries();
  const invalidateTimesheetRowsQuery = useInvalidateGetTimesheetRows();
  const invalidateTimesheetQuery = useInvalidateTimesheet();
  const handleApprovals = useCallback(
    (notification: PushNotification) => {
      void invalidateTimesheetsQuery();
      void invalidateEntriesQuery();
      const invalidateTs = (timesheetId: string) => {
        void invalidateTimesheetQuery(timesheetId);
        void invalidateTimesheetRowsQuery(timesheetId);
      };

      if ('timesheets' in notification.data && Array.isArray(notification.data.timesheets)) {
        for (const timesheetId of notification.data.timesheets) {
          void invalidateTs(timesheetId);
        }
      } else if ('timesheetMapping' in notification.data) {
        const { timesheetMapping } = notification.data as {
          timesheetMapping: Record<string, { timesheetId: string }>;
        };
        const timesheetIds = [...new Set(Object.values(timesheetMapping).map((mapping) => mapping.timesheetId))];
        for (const timesheetId of timesheetIds) {
          invalidateTs(timesheetId);
        }
      }
    },
    [invalidateEntriesQuery, invalidateTimesheetQuery, invalidateTimesheetRowsQuery, invalidateTimesheetsQuery],
  );

  useOnLatestNotification(NotificationTopic.APPROVALS, handleApprovals);
  const handleSave = useCallback((notification: PushNotification) => {
    onSaveSettled(notification.data.timesheetId, notification.type === NotificationType.SUCCESS);
  }, []);
  useOnLatestNotification(NotificationTopic.SAVE_TIMESHEET, handleSave);
  const handleSubmit = useCallback(
    (notification: PushNotification) => {
      onSubmitSettled(notification.data.timesheetId, notification.type === NotificationType.SUCCESS);
      if ('timesheetId' in notification.data) {
        void invalidateTimesheetQuery(notification.data.timesheetId);
      }
    },
    [onSubmitSettled, invalidateTimesheetQuery],
  );
  useOnLatestNotification(NotificationTopic.SUBMIT_TIMESHEET, handleSubmit);
}

export function NotificationGlobalHandler({ children }: PropsWithChildren<Record<string, unknown>>) {
  useHandleGlobalNotifications();
  return <>{children}</>;
}
